import { defineStore } from 'pinia'
import { ref, watch, type Ref } from 'vue'

/**
 * The label for the crumb you are standing on.
 *
 * Only detail screens need this: the shell knows a route is called "Ordre", but only the screen
 * knows it is order 10242. A store rather than a prop because the shell renders the trail and the
 * view is two levels below it inside `<RouterView>`.
 *
 * Cleared on every navigation, so a stale number from the previous screen can never be shown next
 * to a different one — which would be worse than no label at all.
 */
export const useBreadcrumbStore = defineStore('breadcrumb', () => {
  const leafLabel = ref<string | null>(null)

  function set(label: string | null) {
    leafLabel.value = label
  }

  function clear() {
    leafLabel.value = null
  }

  return { leafLabel, set, clear }
})

/**
 * Keeps the last crumb in step with something the screen loaded.
 *
 * One line in a detail view: `useCrumbLabel(() => order.value && \`Ordre ${order.value.orderNumber}\`)`.
 * Nothing to clear on unmount — the router clears it on every navigation.
 */
export function useCrumbLabel(source: Ref<string | null | undefined> | (() => string | null | undefined)) {
  const store = useBreadcrumbStore()

  watch(
    source,
    (label) => store.set(label ?? null),
    { immediate: true },
  )
}
